import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import Spinner from "@/components/Spinner";
import clsx from "clsx";
import { SITE_URL } from "@/lib/siteConfig";

const API = "/api/public";

export default function BlogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "";
  const [posts, setPosts] = useState<any[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const qs = category ? `?category=${encodeURIComponent(category)}` : "";
    fetch(`${API}/blog${qs}`)
      .then((r) => r.json())
      .then((data) => { setPosts(data.posts || []); setCategories(data.categories || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [category]);

  return (
    <>
      <SeoHead
        title={category ? `${category} — MoreMail Blog` : "Blog — MoreMail"}
        description="Email marketing tips, strategies, and product updates from the MoreMail team."
        url={`${SITE_URL}/blog`}
        breadcrumbs={[
          { name: "Home", url: SITE_URL },
          { name: "Blog", url: `${SITE_URL}/blog` },
        ]}
      />

      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="font-display text-4xl font-bold mb-3">Blog</h1>
        <p className="text-lg text-slate mb-8">Email marketing insights, strategies, and product updates.</p>

        {/* Categories */}
        {categories.length > 0 && (
          <div className="flex gap-2 mb-8 flex-wrap">
            <button onClick={() => setSearchParams({})}
              className={clsx("px-4 py-1.5 rounded-full text-xs font-semibold",
                !category ? "bg-ink text-white" : "bg-white text-slate border border-black/[0.06] hover:border-black/20"
              )}>All</button>
            {categories.map((c) => (
              <button key={c} onClick={() => setSearchParams({ category: c })}
                className={clsx("px-4 py-1.5 rounded-full text-xs font-semibold capitalize",
                  category === c ? "bg-ink text-white" : "bg-white text-slate border border-black/[0.06] hover:border-black/20"
                )}>{c}</button>
            ))}
          </div>
        )}

        {loading ? <Spinner className="py-20" /> : posts.length === 0 ? (
          <p className="text-center text-slate py-20">No posts yet. Check back soon!</p>
        ) : (
          <div className="space-y-8">
            {posts.map((post) => (
              <Link key={post.id} to={`/blog/${post.slug}`}
                className="block bg-white rounded-2xl border border-black/[0.06] p-6 hover:shadow-lg transition-shadow group">
                <div className="flex items-center gap-3 mb-3">
                  {post.category && (
                    <span className="px-2.5 py-0.5 bg-coral-light text-coral text-xs font-semibold rounded-full">{post.category}</span>
                  )}
                  <span className="text-xs text-slate">
                    {new Date(post.published_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                  </span>
                  <span className="text-xs text-slate">by {post.author_name}</span>
                </div>
                <h2 className="text-xl font-bold mb-2 group-hover:text-coral transition-colors">{post.title}</h2>
                {post.excerpt && <p className="text-slate text-sm leading-relaxed">{post.excerpt}</p>}
                <div className="flex gap-2 mt-3">
                  {(post.tags || []).map((t: string) => (
                    <span key={t} className="px-2 py-0.5 bg-paper rounded text-[11px] font-semibold text-slate">{t}</span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
